import React, { useState, useEffect, useContext, useMemo } from 'react';
import { GenerateSpendCubeContext } from 'contexts/GenerateSpendCubeContext';
import {
	ActionsWrapper,
	AddCustomColumn,
	ColumnContainer,
	ColumnDescriptionTooltip,
	ErrorText,
	HeaderWrapper,
	Heading,
	IconWrapper,
	InfoWrapper,
	MappingViewWrapper,
	Note,
	PaginationWrapper,
	RemoveCustomColumn,
	SaveMapping,
	SourceLabel,
	StyledSelect,
	SubActionsWrapper,
	Wrapper,
	Note2,
} from './ColumnMapping.styles';
import { ApIcon, ApInput, colors } from '@alixpartners/ui-components';
import Pagination from 'components/Pagination/Pagination';
import { Label } from '../FileSelector/FileSelector.styles';
import { MAX_CUSTOM_COLUMNS, predefinedColumns } from 'Data/constants';
import { Tooltip } from 'react-tippy';
import { workingCapitalCols } from '../../constants';

const ColumnMapping = ({
	selectedFile,
	selectedSheet,
	mapping,
	setMapping,
	setIsProceedDisabled,
	finalColumnMapping,
	isWorkingCapitalColsExists,
	setIsWorkingCapitalColsExists,
}) => {
	const { updateMapping, getMapping } = useContext(GenerateSpendCubeContext);
	const [errors, setErrors] = useState({});
	const [customColumns, setCustomColumns] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [isSaveDisabled, setIsSaveDisabled] = useState(true);
	const pageSize = useMemo(() => 5, []);

	const totalColumns = useMemo(
		() => {
			const mappedCols = finalColumnMapping?.excels;

			const workingCapitalColsExists = mappedCols.some(excel =>
				excel?.sheets.some(sheet =>
					sheet?.mapping.some(data => workingCapitalCols.includes(data.source))
				)
			);
			setIsWorkingCapitalColsExists(workingCapitalColsExists);
			let alteredColumns = [...predefinedColumns];
			if (Object.keys(mapping).includes('PARENT_LEGAL_ENTITY')) {
				if (mapping?.PARENT_LEGAL_ENTITY?.destination === 'OTHER') {
					const filteredColumns = predefinedColumns.filter(
						x => x.columnName !== 'PURCHASING_LEGAL_ENTITY'
					);
					alteredColumns = [...filteredColumns];
				}
			}
			if (
				Object.keys(mapping).includes('Payment_Date') ||
				Object.keys(mapping).includes('Receive_Date') ||
				Object.keys(mapping).includes('Invoice_Date') ||
				Object.keys(mapping).includes('Invoice_Number') ||
				Object.keys(mapping).includes('site') ||
				isWorkingCapitalColsExists
			) {
				const columnsForWorkingCapital = alteredColumns.map(cols => {
					if (workingCapitalCols.includes(cols.columnName)) {
						return { ...cols, required: true };
					}
					return { ...cols };
				});
				return [...columnsForWorkingCapital, ...customColumns];
			}
			return [...alteredColumns, ...customColumns];
		}, //eslint-disable-next-line
		[predefinedColumns, mapping, customColumns]
	);

	const totalCount = useMemo(() => totalColumns.length, [totalColumns]);
	const totalPages = useMemo(() => Math.ceil(totalCount / pageSize), [
		totalCount,
		pageSize,
	]);

	useEffect(
		() => {
			const existingMapping = getMapping(
				selectedFile.excelName,
				selectedSheet.sheetName
			);
			setCurrentPage(1);

			if (existingMapping.length > 0) {
				const mappedData = existingMapping.reduce((acc, item) => {
					acc[item.source] = { ...item };
					return acc;
				}, {});
				setMapping(mappedData);

				const customCols = [];
				existingMapping.forEach(data => {
					const regex = /CUSTOM_DATA(\d+)/;
					const match = data.source.match(regex);
					if (match) {
						const newCustomColumn = {
							columnName: `CUSTOM_DATA${match[1]}`,
							displayName: `Custom Column ${match[1]}`,
							required: false,
							type: 'custom',
							description: `Custom Data ${match[1]}`,
							number: Number(match[1]),
						};
						customCols.push(newCustomColumn);
					}
				});
				setCustomColumns(customCols);
			} else {
				setMapping({});
			}
		}, //eslint-disable-next-line
		[selectedFile, selectedSheet]
	);

	useEffect(
		() => {
			const validated = !validateFields(false, mapping);
			setIsSaveDisabled(validated);
			if (validated) {
				setIsProceedDisabled(validated);
			}
		}, //eslint-disable-next-line
		[totalColumns, mapping]
	);

	const handleMappingChange = (source, destination) => {
		let currentMapping = mapping;
		const changedMapping = {
			...currentMapping,
			[source]: { source, destination },
		};
		if (source === 'PARENT_LEGAL_ENTITY' && destination !== 'OTHER') {
			const filteredMapping = Object.fromEntries(
				Object.entries(changedMapping).filter(
					([key, value]) => key !== 'PURCHASING_LEGAL_ENTITY'
				)
			);
			setMapping(filteredMapping);
		} else {
			setMapping(changedMapping);
		}

		if (destination !== '') {
			setErrors(prev => ({ ...prev, [source]: '' }));
		}
	};

	const handleOtherChange = (source, value) => {
		if (!/^[A-Za-z_ ]+$/.test(value)) {
			setErrors(prev => ({
				...prev,
				[source]: 'Only alphabets and underscores(_) are allowed',
			}));
		} else {
			setErrors(prev => ({ ...prev, [source]: '' }));
		}

		const currentMapping = mapping;
		if (source === 'PARENT_LEGAL_ENTITY') {
			const changedMapping = {
				...currentMapping,
				[source]: { source, destination: 'OTHER', value: value?.trim() },
				PURCHASING_LEGAL_ENTITY: {
					source: 'PURCHASING_LEGAL_ENTITY',
					destination: 'OTHER',
					value: value?.trim(),
				},
			};
			setMapping(changedMapping);
		} else {
			const changedMapping = {
				...currentMapping,
				[source]: { source, destination: 'OTHER', value: value?.trim() },
			};
			setMapping(changedMapping);
		}
	};

	const validateFields = (isSaving, mapping) => {
		let isValid = true;
		let newErrors = {};

		totalColumns.forEach(col => {
			if (
				col.required &&
				(!mapping[col.columnName] || !mapping[col.columnName].destination)
			) {
				if (isSaving) {
					newErrors[col.columnName] = `${col.displayName} is required`;
				}
				isValid = false;
			}

			if (
				mapping[col.columnName]?.destination === 'OTHER' &&
				mapping[col.columnName]?.source === 'PARENT_LEGAL_ENTITY' &&
				(!mapping[col.columnName].value ||
					mapping[col.columnName].value.trim() === '')
			) {
				if (isSaving) {
					newErrors[col.columnName] = 'Custom value is required';
				}
				isValid = false;
			} else if (
				mapping[col.columnName]?.destination === 'OTHER' &&
				mapping[col.columnName]?.source === 'PARENT_LEGAL_ENTITY' &&
				!/^[A-Za-z_ ]+$/.test(mapping[col.columnName]?.value)
			) {
				newErrors[col.columnName] =
					'Only alphabets and underscores(_) are allowed';
				isValid = false;
			}
		});

		setErrors(newErrors);
		return isValid;
	};

	const handleSubmit = () => {
		if (!validateFields(true, mapping)) {
			setIsProceedDisabled(true);
			return;
		}

		const finalMapping = Object.values(mapping);
		updateMapping(
			selectedFile.excelName,
			selectedSheet.sheetName,
			finalMapping
		);
		setIsProceedDisabled(false);
	};

	const addCustomColumn = () => {
		if (customColumns.length > MAX_CUSTOM_COLUMNS) return;

		const lastIndex = customColumns.length - 1;
		const currentCounter = customColumns[lastIndex]
			? customColumns[lastIndex].number + 1
			: 1;

		const newCustomColumn = {
			columnName: `CUSTOM_DATA${currentCounter}`,
			displayName: `Custom Column ${currentCounter}`,
			required: false,
			type: 'custom',
			description: `Custom Data ${currentCounter}`,
			number: currentCounter,
		};

		setCustomColumns(prev => [...prev, newCustomColumn]);
		setCurrentPage(Math.ceil((totalCount + 1) / pageSize));
	};

	const removeCustomColumn = columnName => {
		setCustomColumns(prev => {
			const updatedColumns = prev.filter(col => col.columnName !== columnName);
			return updatedColumns;
		});

		setMapping(prev => {
			const newMapping = { ...prev };
			delete newMapping[columnName];
			return newMapping;
		});

		setCurrentPage(prev =>
			Math.min(prev, Math.ceil((totalCount - 1) / pageSize) || 1)
		);
	};

	const removeMappedColumn = columnName => {
		setMapping(prev => {
			const newMapping = { ...prev };
			delete newMapping[columnName];
			return newMapping;
		});
	};

	const currentColumns = totalColumns.slice(
		(currentPage - 1) * pageSize,
		currentPage * pageSize
	);

	return (
		<Wrapper>
			<HeaderWrapper>
				<Heading>
					Map Columns for File: {selectedFile?.excelName} & Sheet:{' '}
					{selectedSheet?.sheetName}
				</Heading>
			</HeaderWrapper>
			<Note>
				<span className="note_heading">Note:</span> Fields marked with a
				<span style={{ color: colors.red600 }}> * </span>are mandatory to be
				mapped to a column name in spend file.
			</Note>
			<Note2>
				To map columns
				<Tooltip
					position="bottom"
					animation="fade"
					html={
						<ColumnDescriptionTooltip>
							Working Capital Columns: Payment Date, Receive Date, Invoice Date
							Working Capital, Invoice Number, Site
						</ColumnDescriptionTooltip>
					}
					trigger="mouseenter"
					theme="light"
				>
					<IconWrapper>
						<ApIcon
							iconName="baseline_help"
							iconSize={16}
							iconColor={colors.grey6}
						/>
					</IconWrapper>
				</Tooltip>
				for Working Capital, all related columns must contain data and be mapped
				for all files and sheets.
			</Note2>
			<Note2>
				Please check page 5 and 6 for all files and sheets for the same before
				proceeding.
			</Note2>

			<MappingViewWrapper>
				{currentColumns.map(col => (
					<ColumnContainer key={col.columnName}>
						<InfoWrapper>
							<SourceLabel required={col.required}>
								{col.displayName}
								{col.required && (
									<span style={{ color: colors.red600 }}>*</span>
								)}
							</SourceLabel>
							<Tooltip
								position="bottom"
								animation="fade"
								html={
									<ColumnDescriptionTooltip>
										{col.description}
									</ColumnDescriptionTooltip>
								}
								trigger="mouseenter"
								theme="light"
							>
								<IconWrapper>
									<ApIcon
										iconName="baseline_help"
										iconSize={16}
										iconColor={colors.grey6}
									/>
								</IconWrapper>
							</Tooltip>
						</InfoWrapper>

						<StyledSelect
							className="column-select"
							onChange={e =>
								handleMappingChange(col.columnName, e.target.value)
							}
							value={mapping[col.columnName]?.destination || ''}
							style={{
								border: errors[col.columnName] && `2px solid ${colors.red600}`,
							}}
						>
							<option value="">-- Select a Column --</option>
							{selectedSheet.columns
								.filter((item, index, self) => {
									return (
										self.findIndex(
											obj => obj.columnName === item.columnName
										) === index
									);
								})
								.sort((a, b) => a.columnName.localeCompare(b.columnName))
								.filter(col => col.columnName !== '')
								.map(sheetCol => (
									<option key={sheetCol.columnName} value={sheetCol.columnName}>
										{sheetCol.columnName}
									</option>
								))}
							{col.columnName === 'PARENT_LEGAL_ENTITY' && (
								<option value="OTHER">Other</option>
							)}
						</StyledSelect>

						{mapping[col.columnName]?.destination === 'OTHER' &&
							mapping[col.columnName]?.source === 'PARENT_LEGAL_ENTITY' && (
								<ApInput
									className="custom-name"
									placeholder="Enter Custom Value"
									onChange={e => handleOtherChange(col.columnName, e)}
									value={mapping[col.columnName]?.value || ''}
								/>
							)}

						{col.type === 'custom' && (
							<RemoveCustomColumn
								iconName="remove"
								onClick={() => removeCustomColumn(col.columnName)}
							>
								Remove
							</RemoveCustomColumn>
						)}

						{col.type !== 'custom' &&
							Object.keys(mapping).includes(col.columnName) && (
								<RemoveCustomColumn
									iconName="remove"
									onClick={() => removeMappedColumn(col.columnName)}
								>
									Deselect Mapping
								</RemoveCustomColumn>
							)}

						{errors[col.columnName] && (
							<ErrorText>{errors[col.columnName]}</ErrorText>
						)}
					</ColumnContainer>
				))}

				<ActionsWrapper>
					<PaginationWrapper>
						<Label>Page:</Label>
						<Pagination
							currentPage={currentPage}
							totalCount={totalCount}
							pageSize={pageSize}
							onPageChange={page => setCurrentPage(page)}
							position="start"
						/>
					</PaginationWrapper>
					<SubActionsWrapper>
						<div>
							{currentPage === totalPages &&
								customColumns.length < MAX_CUSTOM_COLUMNS && (
									<AddCustomColumn
										onClick={addCustomColumn}
										iconName="baseline_add"
									>
										Add Custom Column
									</AddCustomColumn>
								)}
						</div>
						<div>
							<SaveMapping
								disabled={isSaveDisabled}
								onClick={() => {
									handleSubmit();
								}}
							>
								Save
							</SaveMapping>
						</div>
					</SubActionsWrapper>
				</ActionsWrapper>
			</MappingViewWrapper>
		</Wrapper>
	);
};

export default ColumnMapping;
